import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  browserSessionPersistence,
  setPersistence,
  signInAnonymously,
} from "firebase/auth";
import { auth } from "../config/firebase";
import { APIURL } from "../config/api";
import {
  IClientData,
  IMilestone,
  IJournalDocument,
  IJournalNote,
  IPension
} from "../models/ClientData";
import { IAnonClientData } from "../models/AnonClientData";
import {
  reconcileSessionDraftWithApiResponse,
  reconcileSessionDraftWithAnonClientResponse,
  buildDraftKey,
  getDraftUserId,
} from "../helpers/SessionStorageHelpers";
import { ITokenPrefillResponse, ILoggedOutFactFindData } from "../models/LoggedOutFormData";
import { IQuote, IQuoteData } from "../models/QuoteData";
import axios, { AxiosRequestConfig } from "axios";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { getSharedMedicalInfoElements } from "../modules/forms/form_schemas_surveyJS/Shared/MedicalInfoSchema";
import { setLeadTrackingData } from "../helpers/setLeadTrackingData";
import { useLogger } from "./LoggerContext";
import {
  cleanupDraftsForOtherUsers,
  clearAppointmentBookingSessionData,
  clearAnonymousQuoteSessionData,
} from "../helpers/SessionStorageHelpers";
import { v4 as uuidv4 } from 'uuid';

interface IPortalContext {
  getPrefillDataFromToken: (token: string) => Promise<any>;
  insertResubmissionAccountExists: (data?: IClientData) => Promise<number>;
  insertPartialLeadData: (data?: IClientData) => Promise<number>;
  insertClientData: (data?: IClientData) => Promise<number>;
  insertAnonymousClientData: (data?: IClientData) => Promise<IAnonClientData | number>;
  returnClientData: () => Promise<number>;
  setClientData: (data: IClientData | undefined) => void;
  clientData: IClientData | undefined;
  anonymousClientData: IAnonClientData | undefined;
  setAnonymousClientData: (data: IAnonClientData | undefined) => void;
  getNewQuotes: (quoteRequestData: IClientData | undefined) => Promise<any>;
  getNewQuickQuotes: (
    quoteRequestData: IClientData | undefined
  ) => Promise<any>;
  getNewAnonymousQuickQuotes: (
    quoteRequestData: IClientData | undefined
  ) => Promise<any>;
  getUnsavedQuickQuotes: (quoteRequestData: IClientData) => Promise<IQuote[] | number>;
  quoteData: IQuoteData | undefined;
  setQuoteData: (data: IQuoteData | undefined) => void;
  clearClientContext: () => Promise<void>;
  apiUpdateClientData: (updateData: IClientData | undefined) => Promise<number>;
  apiUpdateLeadData: (updateData: IClientData | undefined) => Promise<number>;
  firstLogin: boolean;
  fetchClientData: () => Promise<number>;
  isLoading: boolean;
  isSlow: boolean;
  setLeadSessionData: (data: IClientData) => void;
  insertLoggedOutFormSubmission: (data: ILoggedOutFactFindData, endpoint: string) => Promise<number>;
  validateLoggedOutFormToken: (token: string, route?: string) => Promise<{ isValid: boolean, statusCode: number, message?: string }>;
}

const PortalContext = createContext<Partial<IPortalContext>>({});

const normalTimeout = 120000;
const longertimeout = 180000;


async function axiosWithTimeout(
  config: AxiosRequestConfig,
  setIsLoading: (val: boolean) => void,
  setIsSlow: (val: boolean) => void,
  logAppInsightsTrace: (trace: string, severityLevel: SeverityLevel) => void,
  SLOW_THRESHOLD_MS: number = 10000
) {
  let slowTimer: any;

  try {
    setIsLoading(true);

    // Start the slow timer
    slowTimer = setTimeout(() => {
      setIsSlow(true);

      // Log that we had a slow request
      logAppInsightsTrace(
        `Slow response time detected: ${config?.url} - Threshold exceeded: ${SLOW_THRESHOLD_MS}`,
        SeverityLevel.Warning
      );
    }, SLOW_THRESHOLD_MS);

    // Make the request
    const response = await axios.request(config);

    // If we get here, request succeeded - clear states
    clearTimeout(slowTimer);
    setIsSlow(false);
    setIsLoading(false);

    return response;
  } catch (err: any) {
    // Reset the state
    clearTimeout(slowTimer);
    setIsSlow(false);
    setIsLoading(false);

    throw err;
  }
}

function currentDraftKey(): string {
  return buildDraftKey(getDraftUserId(auth.currentUser));
}

export function PortalContextProvider({ children }: { children: ReactNode }) {
  const { logAppInsightsError, logAppInsightsTrace } = useLogger();
  const [firebaseUser, loading, error] = useAuthState(auth);
  const user = firebaseUser && !firebaseUser.isAnonymous ? firebaseUser : null;
  const [firstLogin, setFirstLogin] = useState(false);

  const [clientData, setClientDataContext] = useState<
    IClientData | undefined
  >();
  const [anonymousClientData, setAnonymousClientData] = useState<
    IAnonClientData | undefined
  >();
  const [quoteData, setQuoteData] = useState<IQuoteData | undefined>(undefined);
  // Track loading and slow states for the entire context
  const [isLoading, setIsLoading] = useState(false);
  const [isSlow, setIsSlow] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) return;

    const currentUserId = user.uid;

    // Clean up all session drafts that do not belong to this user
    cleanupDraftsForOtherUsers(currentUserId);
    clearAppointmentBookingSessionData();

    clearAnonymousQuoteSessionData();
    try {
      window.localStorage.removeItem("emailForSignIn");
    } catch {
      // ignore
    }
  }, [user, loading]);

  async function ensureAnonymousUser() {
    if (auth.currentUser?.isAnonymous) {
      return auth.currentUser;
    }

    if (auth.currentUser && !auth.currentUser.isAnonymous) {
      return auth.currentUser;
    }

    await setPersistence(auth, browserSessionPersistence);
    const credential = await signInAnonymously(auth);
    return credential.user;
  }

  const setLeadSessionData = (data: IClientData) => {
    // Handle sessionId - check URL first, then generate if not present
    let _sessionID = data.sessionId || "";

    // helper function pass in client data populates fields present

    // Check if sessionId is in URL (we dont know why this is here -chris
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.forEach((value, key) => {
      if (key.toLowerCase() === "sid") {
        _sessionID = value;
      }
    });

    // If still not set, generate a new UUID
    if (!_sessionID) {
      _sessionID = uuidv4();
    }

    // Always ensure sessionId, transferToID and token are set
    data.sessionId = _sessionID;
    return data;
  };

  const setClientData = (data: IClientData | undefined) => {
    if (!data) {
      setClientDataContext(undefined);
      return;
    }
    // if no lead source then regsource, if no regsource then portal, medium search
    const leadSessionData = setLeadSessionData(data);
    Object.assign(data, leadSessionData);

    // set tracking data - cookie consent handled in hook
    const trackingData = setLeadTrackingData(data);
    Object.assign(data, trackingData);

    // Default previousNames yes/no
    let previousNamesYesNo = "false";
    let person2PreviousNamesYesNo = "false";

    if (data.previousNames && data.previousNames.length > 0) {
      previousNamesYesNo = "true";
    }

    if (data.person2PreviousNames && data.person2PreviousNames.length > 0) {
      person2PreviousNamesYesNo = "true";
    }

    // Default smoking logic
    let isPreviousSmoker = "false";
    let isCurrentOrPreviousSmoker = "false";

    if (
      (data.medicalInformation?.yearMonthStartedSmoking &&
        data.medicalInformation?.yearMonthStartedSmoking.length > 0 &&
        (!data.medicalInformation?.yearMonthStoppedSmoking ||
          data.medicalInformation?.yearMonthStoppedSmoking.length === 0)) ||
      data.medicalInformation?.previousOrCurrentSmokerYesNo === "true"
    ) {
      isCurrentOrPreviousSmoker = "true";
    }

    if (
      data.medicalInformation?.yearMonthStartedSmoking &&
      data.medicalInformation?.yearMonthStartedSmoking.length > 0 &&
      data.medicalInformation?.yearMonthStoppedSmoking &&
      data.medicalInformation?.yearMonthStoppedSmoking.length > 0
    ) {
      isPreviousSmoker = "true";
    }

    let isPerson2PreviousSmoker = "false";
    let isPerson2CurrentOrPreviousSmoker = "false";

    if (
      data.person2MedicalInformation?.yearMonthStartedSmoking &&
      data.person2MedicalInformation?.yearMonthStartedSmoking.length > 0 &&
      (!data.person2MedicalInformation?.yearMonthStoppedSmoking ||
        data.person2MedicalInformation?.yearMonthStoppedSmoking.length === 0)
    ) {
      isPerson2CurrentOrPreviousSmoker = "true";
    }

    if (
      data.person2MedicalInformation?.yearMonthStartedSmoking &&
      data.person2MedicalInformation?.yearMonthStartedSmoking.length > 0 &&
      data.person2MedicalInformation?.yearMonthStoppedSmoking &&
      data.person2MedicalInformation?.yearMonthStoppedSmoking.length > 0
    ) {
      isPerson2PreviousSmoker = "true";
    }

    // Default medical conditions yes/no
    let hasMedicalConditionsYesNo = "false";
    let hasMedicationsYesNo = "false";

    if (
      data.medicalInformation &&
      Array.isArray(data.medicalInformation.medicalConditionList) &&
      data.medicalInformation.medicalConditionList.length > 0
    ) {
      hasMedicalConditionsYesNo = "true";
    }

    if (
      data.medicalInformation &&
      Array.isArray(data.medicalInformation.medicationList) &&
      data.medicalInformation.medicationList.length > 0
    ) {
      hasMedicationsYesNo = "true";

      data.medicalInformation.medicationList.forEach((medication) => {
        if (medication.endDate) {
          medication.stillTakeMedication = "false";
        } else {
          medication.stillTakeMedication = "true";
        }

        if (medication.dosage && parseInt(medication.dosage) > 0) {
          medication.dosageKnown = "true";
        } else {
          medication.dosageKnown = "false";
        }
      });
    }

    let person2HasMedicalConditionsYesNo = "false";
    let person2HasMedicationsYesNo = "false";

    if (
      data.person2MedicalInformation &&
      Array.isArray(data.person2MedicalInformation.medicalConditionList) &&
      data.person2MedicalInformation.medicalConditionList.length > 0
    ) {
      person2HasMedicalConditionsYesNo = "true";
    }

    if (
      data.person2MedicalInformation &&
      Array.isArray(data.person2MedicalInformation.medicationList) &&
      data.person2MedicalInformation.medicationList.length > 0
    ) {
      person2HasMedicationsYesNo = "true";

      data.person2MedicalInformation.medicationList.forEach((medication) => {
        if (medication.endDate) {
          medication.stillTakeMedication = "false";
        } else {
          medication.stillTakeMedication = "true";
        }

        if (medication.dosage && parseInt(medication.dosage) > 0) {
          medication.dosageKnown = "true";
        } else {
          medication.dosageKnown = "false";
        }
      });
    }

    // We need to get the fields from medicalconditionsharedinfoschema and put them under medinfo instead
    let sharedMedicalConditionInfo: { [key: string]: any } = {};
    if (
      data?.medicalInformation &&
      Array.isArray(data?.medicalInformation?.medicalConditionList) &&
      data?.medicalInformation?.medicalConditionList?.length > 0
    ) {
      // Get shared medical info elements from SurveyJS schema
      const sharedInfoElements = getSharedMedicalInfoElements(
        "medicalInformation",
        false
      );

      // Extract property names from the SurveyJS elements
      const propertyNames = sharedInfoElements
        .map((element: { name: string }) => element.name)
        .filter((name: string) => name.startsWith("medicalInformation."))
        .map((name: string) => name.replace("medicalInformation.", ""));

      propertyNames.forEach((propertyName: string) => {
        data.medicalInformation?.medicalConditionList?.forEach(
          (medicalCondition: any) => {
            const propertyValue = medicalCondition[propertyName];

            if (propertyValue !== undefined) {
              sharedMedicalConditionInfo[propertyName] = propertyValue;
            }
          }
        );
      });
    }

    let sharedMedicalConditionInfoPerson2: { [key: string]: any } = {};
    if (
      data?.person2MedicalInformation &&
      Array.isArray(data?.person2MedicalInformation?.medicalConditionList) &&
      data?.person2MedicalInformation?.medicalConditionList?.length > 0
    ) {
      // Get shared medical info elements from SurveyJS schema
      const sharedInfoElements = getSharedMedicalInfoElements(
        "person2MedicalInformation",
        true
      );

      // Extract property names from the SurveyJS elements
      const propertyNames = sharedInfoElements
        .map((element: { name: string }) => element.name)
        .filter((name: string) => name.startsWith("person2MedicalInformation."))
        .map((name: string) => name.replace("person2MedicalInformation.", ""));

      propertyNames.forEach((propertyName: string) => {
        data.person2MedicalInformation?.medicalConditionList?.forEach(
          (medicalCondition: any) => {
            const propertyValue = medicalCondition[propertyName];

            if (propertyValue !== undefined) {
              sharedMedicalConditionInfoPerson2[propertyName] = propertyValue;
            }
          }
        );
      });
    }

    // Map client journal notes and docs
    let clientJournalDocs: Array<IJournalDocument> = [];

    if (Array.isArray(data.journalNotes) && data.journalNotes.length > 0) {
      data.journalNotes.forEach((journalNote: IJournalNote) => {
        for (let i = 0; i < 11; i++) {
          const journalFileName = `attachment${i}Name`;
          const journalFile = `attachment${i}`;

          if (
            journalNote[journalFileName] &&
            journalNote[journalFileName] !== ""
          ) {
            clientJournalDocs.push({
              name: journalNote[journalFileName],
              file: journalNote[journalFile],
              date: journalNote.date,
              type: journalNote[journalFileName].split(".")[1],
              redirectURL:
                journalNote[journalFileName].requiresSignature === "true" &&
                  journalNote[journalFileName].docusignEnvelopeID !== null
                  ? journalNote[journalFileName].docusignEnvelopeID
                  : null,
              requiresSignature: journalNote[journalFileName].requiresSignature,
            });
          }
        }
      });
    }

    // Map Annuity journal notes and docs
    let annuityJournalDocs: Array<IJournalDocument> = [];

    if (
      data.annuity &&
      Array.isArray(data.annuity.journalNotes) &&
      data.annuity.journalNotes.length > 0
    ) {
      data.annuity.journalNotes.forEach((journalNote: IJournalNote) => {
        for (let i = 0; i < 11; i++) {
          const journalFileName = `attachment${i}Name`;
          const journalFile = `attachment${i}`;

          if (
            journalNote[journalFileName] &&
            journalNote[journalFileName] !== ""
          ) {
            annuityJournalDocs.push({
              name: journalNote[journalFileName],
              file:
                journalNote.requiresSignature === "true"
                  ? ""
                  : journalNote[journalFile],
              date: journalNote.date,
              type:
                journalNote.requiresSignature === "true"
                  ? "pdf"
                  : journalNote[journalFileName].split(".")[1],
              redirectURL:
                journalNote.requiresSignature === "true" &&
                  journalNote.redirectUrl !== null
                  ? journalNote.redirectUrl
                  : null,
              requiresSignature: journalNote.requiresSignature === "true",
            });
          }
        }
      });
    }

    // Map Pensions
    let pensions: Array<IPension> = [];

    if (
      data.pensions &&
      Array.isArray(data.pensions) &&
      data.pensions.length > 0
    ) {
      pensions = data.pensions;

      // Map Pension journal notes and docs
      pensions.forEach((pension) => {
        let pensionJournalDocs: Array<IJournalDocument> = [];

        if (
          pension &&
          Array.isArray(pension.journalNotes) &&
          pension.journalNotes.length > 0
        ) {
          pension.journalNotes.forEach((journalNote: IJournalNote) => {
            for (let i = 0; i < 11; i++) {
              const journalFileName = `attachment${i}Name`;
              const journalFile = `attachment${i}`;

              if (
                journalNote[journalFileName] &&
                journalNote[journalFileName] !== ""
              ) {
                pensionJournalDocs.push({
                  name: journalNote[journalFileName],
                  file: journalNote[journalFile],
                  date: journalNote.date,
                  type: journalNote[journalFileName].split(".")[1],
                  redirectURL:
                    journalNote.requiresSignature === "true" &&
                      journalNote.docusignEnvelopeID !== null
                      ? journalNote.docusignEnvelopeID
                      : null,
                  requiresSignature: journalNote.requiresSignature,
                });
              }
            }
          });

          pension.journalDocuments = pensionJournalDocs;
        }
      });
    }

    // Update annuity client1OrJoint field to match client1OrJoint on personal details
    if (data.annuity) {
      data.client1OrJoint = data.annuity.clientId;
    }

    // Handle employment values
    if (data?.employments && data?.employments.length > 0) {
      data?.employments.forEach((employment) => {
        if (
          employment.occupation === "3243" ||
          employment.occupation === "3585"
        ) {
          employment.employmentType = "N/A";
        }
      });
    }

    // Handle annuity protection defaults
    if (data.annuity) {
      if (Number(data.annuity.valueProtection) > 0 && Number(data.annuity.guaranteePeriod) <= 0) {
        data.annuity.protectionType = "valueProtection"
        data.annuity.guaranteePeriod = "0";
      }

      else if (Number(data.annuity.guaranteePeriod) > 0 && Number(data.annuity.valueProtection) <= 0) {
        data.annuity.protectionType = "guaranteePeriod"
        data.annuity.valueProtection = "0";
      }
      else {
        data.annuity.protectionType = "none"
        data.annuity.valueProtection = "0";
        data.annuity.guaranteePeriod = "0";
      }
    }

    const legacyAnnuityMilestones: Array<IMilestone> = [
      {
        name: "IFA advice & recommendation",
        completed: data.annuity?.iFACallCompletedDate ? true : false,
        completedDate:
          data.annuity?.iFACallCompletedDate?.replace("00:00:00", "") ?? "",
      },
      {
        name: "Pension checks",
        completed: data.annuity?.pensionChecksCompletedDate ? true : false,
        completedDate:
          data.annuity?.pensionChecksCompletedDate?.replace("00:00:00", "") ?? "",
      },
      {
        name: "Application pack sent",
        completed: data.annuity?.applicationPackSentToClientDate ? true : false,
        completedDate:
          data.annuity?.applicationPackSentToClientDate?.replace(
            "00:00:00",
            ""
          ) ?? "",
      },
      {
        name: "Application pack back",
        completed: data.annuity?.applicationPackBackDate ? true : false,
        completedDate:
          data.annuity?.applicationPackBackDate?.replace("00:00:00", "") ?? "",
      },
      {
        name: "Application submitted to provider",
        completed: data.annuity?.applicationSubmittedToProviderDate ? true : false,
        completedDate:
          data.annuity?.applicationSubmittedToProviderDate?.replace(
            "00:00:00",
            ""
          ) ?? "",
      },
      {
        name: "Completion",
        completed: data.annuity?.completedDate ? true : false,
        completedDate: data.annuity?.completedDate?.replace("00:00:00", "") ?? "",
      },
    ];

    const crmAnnuityMilestones: Array<IMilestone> = Array.isArray(data.annuity?.milestones)
      ? (data.annuity?.milestones as Array<IMilestone>)
      : [];

    // Keep CRM milestones (with IDs) and add legacy display milestones if not already present.
    const mergedAnnuityMilestones: Array<IMilestone> = [...crmAnnuityMilestones];
    legacyAnnuityMilestones.forEach((legacyMilestone) => {
      const alreadyExists = mergedAnnuityMilestones.some(
        (existing) =>
          (legacyMilestone.milestoneId &&
            existing.milestoneId === legacyMilestone.milestoneId) ||
          (legacyMilestone.name && existing.name === legacyMilestone.name)
      );

      if (!alreadyExists) {
        mergedAnnuityMilestones.push(legacyMilestone);
      }
    });

    // Build data with defaults
    const clientDataWithDefaults = {
      ...data,
      hasPreviousNamesYesNo: previousNamesYesNo,
      person2HasPreviousNamesYesNo: person2PreviousNamesYesNo,
      journalDocuments: clientJournalDocs,
      medicalInformation: {
        ...(data.medicalInformation ?? {}),
        ...sharedMedicalConditionInfo,
        medicalConditionsYesNo: hasMedicalConditionsYesNo,
        medicationsYesNo: hasMedicationsYesNo,
        previousSmokerYesNo: isPreviousSmoker,
        previousOrCurrentSmokerYesNo:
          isPreviousSmoker === "true" || isCurrentOrPreviousSmoker === "true"
            ? "true"
            : "false",
      },
      person2MedicalInformation: {
        ...(data.person2MedicalInformation ?? {}),
        ...sharedMedicalConditionInfoPerson2,
        medicalConditionsYesNo: person2HasMedicalConditionsYesNo,
        medicationsYesNo: person2HasMedicationsYesNo,
        previousSmokerYesNo: isPerson2PreviousSmoker,
        previousOrCurrentSmokerYesNo:
          isPerson2PreviousSmoker === "true" ||
            isPerson2CurrentOrPreviousSmoker === "true"
            ? "true"
            : "false",
      },
      pensions: pensions,
      annuity: {
        ...data.annuity,
        milestones: mergedAnnuityMilestones,
        journalDocuments: annuityJournalDocs,
        escalationRate: data.annuity?.escalationRate,
        paymentFrequency: data.annuity?.paymentFrequency,
        paymentTiming: data.annuity?.paymentTiming,
        guaranteePeriod: data.annuity?.guaranteePeriod,
        reversionaryType: data.annuity?.reversionaryType,
        reversionaryBenefit: data.annuity?.reversionaryBenefit,
        reversionaryStart: data.annuity?.reversionaryStart,
      },
    };
    setClientDataContext(clientDataWithDefaults);
  };

  async function apiUpdateClientData(
    updateData: IClientData | undefined
  ): Promise<number> {
    let responseData: IClientData = {};
    if (updateData) {
      // Update previousNames yes/no
      if (updateData.hasPreviousNamesYesNo === "false") {
        updateData.previousNames = "";
        updateData.previousNamesDateChanged = "";
      }

      if (updateData.person2HasPreviousNamesYesNo === "false") {
        updateData.person2PreviousNames = "";
        updateData.person2PreviousNamesDateChanged = "";
      }

      if (updateData.medicalInformation) {
        let medicalInfo: any = updateData.medicalInformation;

        // Default previousSmoker yes/no and previousOrCurrentSmoker yes/no
        if (medicalInfo.previousOrCurrentSmokerYesNo === "false") {
          medicalInfo.yearMonthStartedSmoking = "";
          medicalInfo.monthStartedSmoking = "";
          medicalInfo.yearStartedSmoking = "";
          medicalInfo.yearMonthStoppedSmoking = "";
          medicalInfo.monthStoppedSmoking = "";
          medicalInfo.yearStoppedSmoking = "";
          medicalInfo.cigarettesPerDay = 0;
          medicalInfo.gramsOfTobaccoPerDay = 0;
          medicalInfo.gramsOfPipeTobaccoPerDay = 0;
          medicalInfo.cigarsPerDay = 0;
          medicalInfo.previousSmokerYesNo = "false";
          medicalInfo.currentSmoker = "false";
          medicalInfo.regularSmoker = "false";
        }

        // Set currentsmoker field
        if (
          medicalInfo.previousOrCurrentSmokerYesNo === "true" &&
          medicalInfo.previousSmokerYesNo === "false"
        ) {
          medicalInfo.currentSmoker = "true";
        }
        if (medicalInfo.previousSmokerYesNo === "false") {
          medicalInfo.yearMonthStoppedSmoking = "";
          medicalInfo.monthStoppedSmoking = "";
          medicalInfo.yearStoppedSmoking = "";
        }

        // Default medical conditions yes/no and medications yes/no
        if (medicalInfo.medicalConditionsYesNo === "false") {
          medicalInfo.medicalConditionList = [];
        }

        if (medicalInfo.medicationsYesNo === "false") {
          medicalInfo.medicationList = [];
        }

        if (
          medicalInfo?.medicalConditionsYesNo === "true" &&
          Array.isArray(medicalInfo?.medicalConditionList) &&
          medicalInfo?.medicalConditionList?.length > 0
        ) {
          // Get shared medical info elements from SurveyJS schema
          const sharedInfoElements = getSharedMedicalInfoElements(
            "medicalInformation",
            false
          );

          // Extract property names from the SurveyJS elements
          const propertyNames = sharedInfoElements
            .map((element: { name: string }) => element.name)
            .filter((name: string) => name.startsWith("medicalInformation."))
            .map((name: string) => name.replace("medicalInformation.", ""));

          // For each property, get the value from medicalInfo and add it to every medical condition
          propertyNames.forEach((propertyName: string) => {
            const propertyValue = medicalInfo[propertyName];

            if (propertyValue !== undefined) {
              medicalInfo.medicalConditionList.forEach(
                (medicalCondition: any) => {
                  medicalCondition[propertyName] = propertyValue;
                }
              );
            }
          });
        }
        // Default medical condition customerProvidedMedicalConditionName when medicalConditionID recognised
        medicalInfo?.medicalConditionList?.forEach((medCondition: any) => {
          if (medCondition.conditionName !== "Unknown") {
            medCondition.customerProvidedMedicalConditionName = "";
          }
        });

        updateData.medicalInformation = medicalInfo;
      }

      if (updateData.person2MedicalInformation) {
        let person2MedicalInfo: any = updateData.person2MedicalInformation;

        if (person2MedicalInfo.previousOrCurrentSmokerYesNo === "false") {
          person2MedicalInfo.yearMonthStartedSmoking = "";
          person2MedicalInfo.monthStartedSmoking = "";
          person2MedicalInfo.yearStartedSmoking = "";
          person2MedicalInfo.yearMonthStoppedSmoking = "";
          person2MedicalInfo.monthStoppedSmoking = "";
          person2MedicalInfo.yearStoppedSmoking = "";
          person2MedicalInfo.cigarettesPerDay = 0;
          person2MedicalInfo.gramsOfTobaccoPerDay = 0;
          person2MedicalInfo.gramsOfPipeTobaccoPerDay = 0;
          person2MedicalInfo.cigarsPerDay = 0;
        }

        if (
          person2MedicalInfo.previousOrCurrentSmokerYesNo === "true" &&
          person2MedicalInfo.previousSmokerYesNo === "false"
        ) {
          person2MedicalInfo.currentSmoker = "true";
        }

        if (person2MedicalInfo.previousSmokerYesNo === "false") {
          person2MedicalInfo.yearMonthStoppedSmoking = "";
          person2MedicalInfo.monthStoppedSmoking = "";
          person2MedicalInfo.yearStoppedSmoking = "";
        }

        if (person2MedicalInfo.medicalConditionsYesNo === "false") {
          person2MedicalInfo.medicalConditionList = [];
        }

        if (person2MedicalInfo.medicationsYesNo === "false") {
          person2MedicalInfo.medicationList = [];
        }

        if (
          person2MedicalInfo?.medicalConditionsYesNo === "true" &&
          Array.isArray(person2MedicalInfo?.medicalConditionList) &&
          person2MedicalInfo?.medicalConditionList?.length > 0
        ) {
          // Get shared medical info elements from SurveyJS schema
          const sharedInfoElements = getSharedMedicalInfoElements(
            "person2MedicalInformation",
            true
          );

          // Extract property names from the SurveyJS elements
          const propertyNames = sharedInfoElements
            .map((element: { name: string }) => element.name)
            .filter((name: string) =>
              name.startsWith("person2MedicalInformation.")
            )
            .map((name: string) =>
              name.replace("person2MedicalInformation.", "")
            );

          // For each property, get the value from medicalInfo and add it to every medical condition
          propertyNames.forEach((propertyName: string) => {
            const propertyValue = person2MedicalInfo[propertyName];

            if (propertyValue !== undefined) {
              person2MedicalInfo.medicalConditionList.forEach(
                (medicalCondition: any) => {
                  medicalCondition[propertyName] = propertyValue;
                }
              );
            }
          });
        }

        // Default medical condition customerProvidedMedicalConditionName when medicalConditionID recognised
        person2MedicalInfo?.medicalConditionList?.forEach(
          (medCondition: any) => {
            if (medCondition.conditionName !== "Unknown") {
              medCondition.customerProvidedMedicalConditionName = "";
            }
          }
        );

        updateData.person2MedicalInformation = person2MedicalInfo;
      }

      // Default annuity quote reversionary fields
      if (updateData?.annuity?.reversionaryTypeYesNo === "false") {
        updateData.annuity.reversionaryType = "5919"; // None
        updateData.annuity.reversionaryBenefit = "100272"; // None
        updateData.annuity.reversionaryStart = "";
      }

      // Default pension customerProvidedCedingSchemeName when cedingSchemeId recognised
      updateData.pensions?.forEach((pension) => {
        if (pension.cedingScheme !== "-1") {
          pension.customerProvidedCedingSchemeName = "";
        }
      });

      // Update annuity client1OrJoint field to match client1OrJoint on personal details
      if (updateData?.annuity) {
        updateData.annuity.clientId = updateData.client1OrJoint;
      }
    }

    if (!updateData) {
      setClientData(responseData);
      return 200;
    }
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const url = `${APIURL}/client/update`;

      const response = await axiosWithTimeout(
        {
          method: "POST",
          url,
          data: updateData,
          headers: {
            "Content-Type": "application/json",
            Authorization: `${idToken}`,
          },
          timeout: normalTimeout, // 30s request timeout
        },
        setIsLoading,
        setIsSlow,
        logAppInsightsTrace,
        10000 // 10 seconds to consider "slow"
      );
      if (auth.currentUser?.isAnonymous) {
        setAnonymousClientData(response.data);

        reconcileSessionDraftWithAnonClientResponse(
          currentDraftKey(),
          response.data
        );
      } else {
        setClientData(response.data);
      }
      return 200;
    } catch (error: any) {

      logAppInsightsError(error, "PortalContext apiUpdateClientData Error");

      // If it's not a 404 or a 500 then mask it as a 500 as that's all we have an error page for
      let errorCode =
        error?.response?.status === 404 || error?.response?.status === 500
          ? error?.response?.status
          : 500;

      return errorCode ?? 500;
    }
  }

  async function apiUpdateLeadData(
    updateData: IClientData | undefined
  ): Promise<number> {
    try {
      //const idToken = await auth.currentUser?.getIdToken();
      const url = `${APIURL}/lead/update`;

      const response = await axiosWithTimeout(
        {
          method: "POST",
          url,
          data: updateData,
          headers: {
            "Content-Type": "application/json",
          },
          timeout: normalTimeout,
        },
        setIsLoading,
        setIsSlow,
        logAppInsightsTrace,
        10000 // 10 seconds to consider "slow"
      );

      reconcileSessionDraftWithApiResponse(
        currentDraftKey(),
        response.data
      );

      return 200;
    } catch (error: any) {
      logAppInsightsError(error, "PortalContext apiUpdateLeadData Error");

      // If it's not a 404 or a 500 then mask it as a 500 as that's all we have an error page for
      let errorCode =
        error?.response?.status === 404 || error?.response?.status === 500
          ? error?.response?.status
          : 500;

      return errorCode ?? 500;
    }
  }

  async function returnClientData() {
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const url = `${APIURL}/client`;
      const response = await axiosWithTimeout(
        {
          method: "GET",
          url,
          headers: {
            Authorization: `${idToken}`,
          },
          timeout: normalTimeout,
        },
        setIsLoading,
        setIsSlow,
        logAppInsightsTrace
      );
      // Success
      if (auth.currentUser?.isAnonymous) {
        setAnonymousClientData(response.data);
      } else {
        setClientData(response.data);
      }
      return 200;
    } catch (error: any) {
      //console.log(error);
      logAppInsightsError(error, "Portal Context - Return Client Data");

      // If it's not a 404 or a 500 then mask it as a 500 as that's all we have an error page for
      let errorCode =
        error?.response?.status === 404 || error?.response?.status === 500
          ? error?.response?.status
          : 500;

      return errorCode ?? 500;
    }
  }

  async function insertClientData(data?: IClientData) {
    try {
      const idToken = await auth.currentUser?.getIdToken();
      let clientJSONObject = data ?? clientData ?? {};
      const url = `${APIURL}/client/insert`;
      // Check if clientData has first, last name and email, set if not before posting from user auth
      clientDataChecks(clientJSONObject);

      const response = await axiosWithTimeout(
        {
          method: "POST",
          url,
          data: clientJSONObject,
          headers: {
            "Content-Type": "application/json",
            Authorization: `${idToken}`,
          },
          timeout: normalTimeout,
        },
        setIsLoading,
        setIsSlow,
        logAppInsightsTrace
      );

      if (auth.currentUser?.isAnonymous) {
        setAnonymousClientData(response.data);
      } else {
        setClientData(response.data);
        setFirstLogin(true);
      }

      return 200;
    } catch (error: any) {
      //console.log(error);
      logAppInsightsError(error, "Portal Context - Insert Client Data");

      // If it's not a 404 or a 500 then mask it as a 500 as that's all we have an error page for
      let errorCode =
        error?.response?.status === 404 || error?.response?.status === 500
          ? error?.response?.status
          : 500;

      return errorCode ?? 500;
    }
  }

  async function insertAnonymousClientData(data?: IClientData): Promise<IAnonClientData | number> {
    try {
      const anonymousFirebaseUser = await ensureAnonymousUser();
      const idToken = await anonymousFirebaseUser.getIdToken();
      let clientJSONObject = data ?? clientData ?? {};
      const url = `${APIURL}/client/insert`;

      clientDataChecks(clientJSONObject);

      const response = await axiosWithTimeout(
        {
          method: "POST",
          url,
          data: clientJSONObject,
          headers: {
            "Content-Type": "application/json",
            Authorization: `${idToken}`,
          },
          timeout: normalTimeout,
        },
        setIsLoading,
        setIsSlow,
        logAppInsightsTrace
      );

      setAnonymousClientData(response.data);
      return response.data;
    } catch (error: any) {
      logAppInsightsError(error, "Portal Context - Insert Anonymous Client Data");

      let errorCode =
        error?.response?.status === 404 || error?.response?.status === 500
          ? error?.response?.status
          : 500;

      return errorCode ?? 500;
    }
  }

  async function insertResubmissionAccountExists(data?: IClientData) {
    try {
      let clientJSONObject = data ?? clientData ?? {};

      // set tracking data - cookie consent handled in hook
      const trackingData = setLeadTrackingData(clientJSONObject);
      Object.assign(clientJSONObject, trackingData);

      const url = `${APIURL}/lead/insertresubmissionaccountexists`;
      // Check if clientData has first, last name and email, set if not before posting from user auth
      //clientDataChecks(clientJSONObject);

      const response = await axios({
        method: "POST",
        url,
        data: clientJSONObject,
        headers: {
          "Content-Type": "application/json",
        },
        timeout: normalTimeout,
      });
      //setClientData(response.data);

      return 200;
    } catch (error: any) {
      //console.log(error);
      logAppInsightsError(error, "Portal Context - Insert Partial Lead Data");

      // If it's not a 404 or a 500 then mask it as a 500 as that's all we have an error page for
      let errorCode =
        error?.response?.status === 404 || error?.response?.status === 500
          ? error?.response?.status
          : 500;

      return errorCode ?? 500;
    }
  }

  async function insertPartialLeadData(data?: IClientData) {
    try {
      let clientJSONObject = data ?? clientData ?? {};

      // set tracking data - cookie consent handled in hook
      const trackingData = setLeadTrackingData(clientJSONObject);
      Object.assign(clientJSONObject, trackingData);

      const url = `${APIURL}/lead/insertpartial`;
      // Check if clientData has first, last name and email, set if not before posting from user auth
      //clientDataChecks(clientJSONObject);

      const response = await axios({
        method: "POST",
        url,
        data: clientJSONObject,
        headers: {
          "Content-Type": "application/json",
        },
        timeout: normalTimeout,
      });
      reconcileSessionDraftWithApiResponse(
        currentDraftKey(),
        response.data
      );

      return 200;
    } catch (error: any) {
      //console.log(error);
      logAppInsightsError(error, "Portal Context - Insert Partial Lead Data");

      // If it's not a 404 or a 500 then mask it as a 500 as that's all we have an error page for
      let errorCode =
        error?.response?.status === 404 || error?.response?.status === 500
          ? error?.response?.status
          : 500;

      return errorCode ?? 500;
    }
  }

  async function fetchClientData() {
    try {
      const idToken = await auth.currentUser?.getIdToken();
      let clientJSONObject = clientData ?? {};
      const url = `${APIURL}/client/retrieve`;

      clientDataChecks(clientJSONObject);

      const response = await axiosWithTimeout(
        {
          method: "POST",
          url,
          data: clientJSONObject,
          headers: {
            "Content-Type": "application/json",
            Authorization: `${idToken}`,
          },
          timeout: longertimeout,
        },
        setIsLoading,
        setIsSlow,
        logAppInsightsTrace
      );

      if (auth.currentUser?.isAnonymous) {
        setAnonymousClientData(response.data);
      } else {
        setClientData(response.data);
        setFirstLogin(true);
      }

      return 200;
    } catch (error: any) {
      //console.log(error);
      logAppInsightsError(error, "Portal Context - Retrieve Client Data");

      // If it's not a 404 or a 500 then mask it as a 500 as that's all we have an error page for
      let errorCode =
        error?.response?.status === 404 || error?.response?.status === 500
          ? error?.response?.status
          : 500;

      return errorCode ?? 500;
    }
  }

  function clientDataChecks(clientJSONObject: IClientData) {
    if (!clientJSONObject?.firstName || clientJSONObject.firstName === null) {
      if (user?.displayName) {
        const firstName =
          user.displayName.indexOf(" ") === -1
            ? user.displayName
            : user.displayName.split(" ")[0];
        clientJSONObject.firstName = firstName;
      }
    }

    if (!clientJSONObject?.lastName || clientJSONObject.lastName === null) {
      if (user?.displayName) {
        const lastName =
          user.displayName.indexOf(" ") === -1
            ? ""
            : user.displayName.split(" ")[1];
        clientJSONObject.lastName = lastName;
      }
    }

    if (!clientJSONObject?.email || clientJSONObject.email === null) {
      if (user?.email) {
        clientJSONObject.email = user.email;
      }
    }
  }

  async function clearClientContext() {
    setClientData(undefined);
  }

  async function getNewQuotes(quoteRequestData: IClientData | undefined) {
    try {
      const idToken = await auth.currentUser?.getIdToken();
      let url = `${APIURL}/quote/iress`;

      const response = await axiosWithTimeout(
        {
          method: "POST",
          url,
          data: quoteRequestData,
          headers: {
            Authorization: `${idToken}`,
          },
          timeout: longertimeout,
        },
        setIsLoading,
        setIsSlow,
        logAppInsightsTrace,
        normalTimeout // Give quotes 30seconds before they are considered slow
      );
      // console.log("clientData after get new quotes", clientData)
      const updatedClientData = {
        ...quoteRequestData,
        annuity: {
          ...quoteRequestData?.annuity,
          fullQuoteCompleted: "true",
          fullQuoteCompletedDate: new Date().toLocaleString("en-GB", {
            timeZone: "Europe/London",
          }),
          quotes: response.data,
        },
      };

      const updateResponse = await apiUpdateClientData(updatedClientData);

      if (updateResponse !== 200) {
        return 500;
      }

      return 200;
    } catch (error: any) {
      //console.log(error);
      logAppInsightsError(error, "Portal Context - Get New Quotes Error");

      // If it's not a 404 or a 500 then mask it as a 500 as that's all we have an error page for
      let errorCode =
        error?.response?.status === 404 || error?.response?.status === 500
          ? error?.response?.status
          : 500;

      return errorCode ?? 500;
    }
  }

  async function getNewQuickQuotes(quoteRequestData: IClientData | undefined) {
    try {
      let response: any = null;
      let url = `${APIURL}/quote/iressquick`;
      if (user) {
        const idToken = await auth.currentUser?.getIdToken();
        response = await axiosWithTimeout(
          {
            method: "POST",
            url,
            data: quoteRequestData,
            headers: {
              Authorization: `${idToken}`,
            },
            timeout: longertimeout,
          },
          setIsLoading,
          setIsSlow,
          logAppInsightsTrace,
          normalTimeout // Give quotes 30seconds before they are considered slow
        );
      } else {
        response = await axiosWithTimeout(
          {
            method: "POST",
            url,
            data: quoteRequestData,
            headers: {},
            timeout: longertimeout,
          },
          setIsLoading,
          setIsSlow,
          logAppInsightsTrace,
          normalTimeout // Give quotes 30seconds before they are considered slow
        );
      }

      const updatedClientData = {
        ...quoteRequestData,
        annuity: {
          ...quoteRequestData?.annuity,
          quotes: response.data,
        },
      };

      //if we're logged in then update the crm
      if (user) {
        const updateResponse = await apiUpdateClientData(updatedClientData);

        if (updateResponse !== 200) {
          return 500;
        }
        return 200;
      } else {
        setClientData(updatedClientData);
        return { statusCode: 200, data: response.data };
      }
    } catch (error: any) {
      //console.log(error);
      logAppInsightsError(error, "Portal Context - Get New Quick Quotes Error");

      // If it's not a 404 or a 500 then mask it as a 500 as that's all we have an error page for
      let errorCode =
        error?.response?.status === 404 || error?.response?.status === 500
          ? error?.response?.status
          : 500;

      return errorCode ?? 500;
    }
  }

  async function getNewAnonymousQuickQuotes(
    quoteRequestData: IClientData | undefined
  ) {
    try {
      const anonymousFirebaseUser = await ensureAnonymousUser();

      // After a refresh this is empty; client/insert then returns the Firebase user's existing client.
      let baseAnonymousClientData = anonymousClientData;

      if (!baseAnonymousClientData) {
        const insertResponse = await insertAnonymousClientData(quoteRequestData);
        if (typeof insertResponse === "number") {
          return insertResponse;
        }

        baseAnonymousClientData = insertResponse;
      }

      const idToken = await anonymousFirebaseUser.getIdToken();
      const url = `${APIURL}/quote/iressquick`;
      const response = await axiosWithTimeout(
        {
          method: "POST",
          url,
          data: quoteRequestData,
          headers: {
            Authorization: `${idToken}`,
          },
          timeout: longertimeout,
        },
        setIsLoading,
        setIsSlow,
        logAppInsightsTrace,
        normalTimeout
      );

      const updatedClientData: IClientData = {
        ...quoteRequestData,
        id: baseAnonymousClientData?.id ?? quoteRequestData?.id,
        sessionId: baseAnonymousClientData?.sessionId ?? quoteRequestData?.sessionId,
        transferToID: baseAnonymousClientData?.transferToID ?? quoteRequestData?.transferToID,
        annuity: {
          ...quoteRequestData?.annuity,
          quotes: response.data,
        },
      };

      const updateResponse = await apiUpdateClientData(updatedClientData);
      if (updateResponse !== 200) {
        return 500;
      }

      setAnonymousClientData((current) => ({
        ...(current ?? {}),
        quotes: response.data,
        annuity: {
          ...(current?.annuity ?? {}),
          ...(updatedClientData.annuity ?? {}),
        },
      }));

      return { statusCode: 200, data: response.data };
    } catch (error: any) {
      logAppInsightsError(error, "Portal Context - Get New Anonymous Quick Quotes Error");

      let errorCode =
        error?.response?.status === 404 || error?.response?.status === 500
          ? error?.response?.status
          : 500;

      return errorCode ?? 500;
    }
  }

  // Quick quotes that are never saved (e.g. the cost of delay calculator before booking): quote/iressquick with no
  // token, even if an anonymous user exists, so they only add a milestone to the visitor's partial lead.
  async function getUnsavedQuickQuotes(quoteRequestData: IClientData): Promise<IQuote[] | number> {
    try {
      const url = `${APIURL}/quote/iressquick`;
      const response = await axiosWithTimeout(
        {
          method: "POST",
          url,
          data: quoteRequestData,
          headers: {
            "Content-Type": "application/json",
          },
          timeout: longertimeout,
        },
        setIsLoading,
        setIsSlow,
        logAppInsightsTrace,
        normalTimeout
      );

      return (response.data ?? []) as IQuote[];
    } catch (error: any) {
      logAppInsightsError(error, "Portal Context - Get Unsaved Quick Quotes Error");

      return error?.response?.status ?? 500;
    }
  }

  async function getPrefillDataFromToken(
    token: string
  ): Promise<ITokenPrefillResponse | {}> {
    if (!token || token.trim() === "") {
      return {};
    }

    try {
      const url = `${APIURL}/lead/getdatafromtoken`;

      const response = await axios.get<ITokenPrefillResponse | null>(url, {
        params: { tk: token },
        headers: {
          Accept: "application/json",
        },
        timeout: normalTimeout,
      });

      // If response.data is null/undefined/empty string, return empty object
      return response.data || {};
    } catch (error: any) {
      console.error("Error fetching prefill data from token:", error);

      logAppInsightsError(
        error,
        "Portal Context - Get Prefill Data From Token Error"
      );

      // return empty object if fails
      return {};
    }
  }

  async function validateLoggedOutFormToken(token: string, route?: string): Promise<{ isValid: boolean, statusCode: number, message?: string }> {
    const url = `${APIURL}/loggedout/client/validate`;
    try {
      const response = await axios.get<boolean>(url, {
        params: { token: token, route: route },
        headers: {
          Accept: "application/json",
        },
        timeout: normalTimeout,
      });
      return {
        isValid: response.status === 200 && response.data === true,
        statusCode: response.status,
        message: response.statusText
      };
    } catch (error: any) {
      // console.log(error);
      return {
        isValid: false,
        statusCode: 500,
        message: error?.message ?? "Error validating token",
      };
    }
  }

  async function insertLoggedOutFormSubmission(
    data: ILoggedOutFactFindData,
    endpoint: string
  ): Promise<number> {
    const url = `${APIURL}/loggedout/${endpoint}`;
    try {
      const response = await axios.post<{ status: number }>(url, data, { timeout: normalTimeout });

      return response.status ?? 500;
    } catch (error: any) {
      return error?.response?.status ?? 500;
    }
  }

  return (
    <PortalContext.Provider
      value={{
        insertResubmissionAccountExists,
        insertPartialLeadData,
        insertClientData,
        insertAnonymousClientData,
        returnClientData,
        setClientData,
        clientData,
        anonymousClientData,
        setAnonymousClientData,
        getNewQuotes,
        getNewQuickQuotes,
        getNewAnonymousQuickQuotes,
        getUnsavedQuickQuotes,
        quoteData,
        setQuoteData,
        clearClientContext,
        apiUpdateClientData,
        apiUpdateLeadData,
        firstLogin,
        fetchClientData,
        isLoading,
        isSlow,
        setLeadSessionData,
        getPrefillDataFromToken,
        insertLoggedOutFormSubmission,
        validateLoggedOutFormToken,
      }}
    >
      {children}
    </PortalContext.Provider>
  );
}

export function usePortal() {
  return useContext(PortalContext);
}
